
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, List, Avatar, Spin, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Estate } from '../../types'; // Adjust the import path as needed
import { useGlobalContext } from '../../store/GlobalContext';
import { fetchEstates, searchEstates } from '../estatelist/estateService';
import debounce from 'lodash.debounce';

const SearchInput = styled(Input.Search)`
  margin-bottom: 1rem;
`;

interface EstateSelectorModalProps {
  visible: boolean;
  onClose: () => void;
}

const EstateSelectorModal: React.FC<EstateSelectorModalProps> = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [estates, setEstates] = useState<Estate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { selectEstate } = useGlobalContext();

  // Debounced search function to optimize API calls
  const debouncedFetchEstates = debounce(async (query: string) => {
    setLoading(true);
    try {
      const data = await searchEstates(query);
      setEstates(data);
    } catch (error: any) {
      console.error(error);
      message.error(error.message || 'Failed to fetch estates');
    }
    setLoading(false);
  }, 500); // 500ms debounce

  useEffect(() => {
    if (visible) {
      debouncedFetchEstates(searchTerm);
    } else {
      // Reset state when modal is closed
      setEstates([]);
      setSearchTerm('');
      form.resetFields();
    }

    // Cleanup on unmount
    return () => {
      debouncedFetchEstates.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (visible) {
      debouncedFetchEstates(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleSelectEstate = (estate: Estate) => {
    selectEstate(estate);
    message.success(`Selected estate: ${estate.estateName}`);
    // reload page
    window.location.reload();
    onClose();
  };

  return (
    <Modal
      title="Select an Estate"
      visible={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <SearchInput
        placeholder="Search estates..."
        enterButton={<SearchOutlined />}
        onSearch={(value) => setSearchTerm(value)}
        allowClear
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      {loading ? (
        <Spin tip="Searching estates..." />
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={estates}
          locale={{ emptyText: 'No estates found' }}
          renderItem={(estate) => (
            <List.Item onClick={() => handleSelectEstate(estate)} style={{ cursor: 'pointer' }}>
              <List.Item.Meta
                avatar={<Avatar src={estate.description || 'https://via.placeholder.com/40'} />}
                title={estate.estateName}
                description={estate.description}
              />
            </List.Item>
          )}
        />
      )}
    </Modal>
  );
};

export default EstateSelectorModal;
